import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { apiUrl } from '../config/api';
import { useNavigate } from 'react-router-dom';
import ClientLink from '../components/ClientLink';
import { useDetailsModal } from '../context/DetailsModalContext';
import { LINK_TEXT } from '../utils/clickable';
import { formatDateTimeDMY } from '../utils/dateUtils';
import { sortRows, makeSortTh, TABLE_HEAD_ROW, TABLE_HEAD_TEXT } from '../components/UI/tableSort';
import { toCurrency } from '../utils/adminMetrics';
import KpiCard from '../components/Admin/KpiCard';
import ChartCard from '../components/Admin/ChartCard';

// The report tables' header cells (amounts and counts on the right).
const RH = { className: `px-4 py-1.5 ${TABLE_HEAD_TEXT}` };
const RH_RIGHT = { ...RH, align: 'right' };

ChartJS.register(...registerables);

const WINDOW_OPTIONS = [
  { value: 6, label: 'Last 6 months' },
  { value: 12, label: 'Last 12 months' },
  { value: 24, label: 'Last 24 months' },
];

const PALETTE = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];


function toInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function toPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0.0%';
  return `${n.toFixed(1)}%`;
}

const Reports = () => {
  const navigate = useNavigate();
  const { canViewLoans } = useDetailsModal();
  // Open Loans filtered to a status (staff who can see loans only).
  const loansWith = (status) => (canViewLoans ? () => navigate(`/manage-loans?status=${encodeURIComponent(status)}`) : null);
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // A click on a column header sorts that table.
  const [statusSort, setStatusSort] = useState(null);
  const [borrowerSort, setBorrowerSort] = useState(null);
  const [monthSort, setMonthSort] = useState(null);
  const statusTh = makeSortTh(statusSort, setStatusSort);
  const borrowerTh = makeSortTh(borrowerSort, setBorrowerSort);
  const monthTh = makeSortTh(monthSort, setMonthSort);

  const authHeader = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` }),
    []
  );

  const disbCollRef = useRef(null);
  const interestRef = useRef(null);
  const borrowersRef = useRef(null);
  const agingRef = useRef(null);
  const purposeRef = useRef(null);

  const load = useCallback(async (m) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(apiUrl(`/api/reports/business-dashboard?months=${m}`), { headers: authHeader });
      setData(res.data);
    } catch (err) {
      setData(null);
      setError(err.response?.data?.error || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(months); }, [months, load]);

  const kpis = data?.kpis || {};
  const trends = data?.trends || [];
  const breakdowns = data?.breakdowns || {};
  const labels = trends.map((t) => t.label);

  const gridColor = 'rgba(148,163,184,0.2)';
  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { labels: { color: '#94a3b8', boxWidth: 12 } } },
    scales: {
      x: { ticks: { color: '#94a3b8' }, grid: { color: gridColor } },
      y: { ticks: { color: '#94a3b8' }, grid: { color: gridColor } },
    },
  };

  const disbCollData = {
    labels,
    datasets: [
      { label: 'Disbursed', data: trends.map((t) => t.disbursedAmount), borderColor: PALETTE[0], backgroundColor: PALETTE[0], tension: 0.3 },
      { label: 'Collected', data: trends.map((t) => t.collectedAmount), borderColor: PALETTE[1], backgroundColor: PALETTE[1], tension: 0.3 },
    ],
  };
  const interestData = {
    labels,
    datasets: [{ label: 'Interest income', data: trends.map((t) => t.interestIncome), borderColor: PALETTE[4], backgroundColor: 'rgba(124,58,237,0.3)', fill: true, tension: 0.3 }],
  };
  const borrowersData = {
    labels,
    datasets: [{ label: 'New borrowers', data: trends.map((t) => t.newBorrowers), backgroundColor: PALETTE[5] }],
  };
  const aging = breakdowns.aging || [];
  const agingData = {
    labels: aging.map((a) => a.bucket),
    datasets: [{ data: aging.map((a) => a.outstanding), backgroundColor: ['#16a34a', '#f59e0b', '#f97316', '#dc2626', '#7f1d1d'] }],
  };
  const purpose = (breakdowns.byPurpose || []).slice(0, 8);
  const purposeData = {
    labels: purpose.map((p) => p.purpose),
    datasets: [{ label: 'Disbursed', data: purpose.map((p) => p.amount), backgroundColor: PALETTE[2] }],
  };

  const exportCsv = () => {
    if (!data) return;
    const lines = [];
    lines.push('Business Dashboard');
    lines.push(`Generated,${data.generatedAt || ''}`);
    lines.push(`Window (months),${data.window?.months || months}`);
    lines.push('');
    lines.push('KPI,Value');
    lines.push(`Active loans,${kpis.activeLoans}`);
    lines.push(`Active clients,${kpis.activeClients}`);
    lines.push(`Outstanding loan book,${kpis.outstandingBook}`);
    lines.push(`PAR30 amount,${kpis.par30Amount}`);
    lines.push(`PAR30 %,${(kpis.par30Pct || 0).toFixed(2)}`);
    lines.push(`PAR90 amount,${kpis.par90Amount}`);
    lines.push(`PAR90 %,${(kpis.par90Pct || 0).toFixed(2)}`);
    lines.push(`YTD disbursed amount,${kpis.ytdDisbursedAmount}`);
    lines.push(`YTD disbursed count,${kpis.ytdDisbursedCount}`);
    lines.push(`YTD collected,${kpis.ytdCollectedAmount}`);
    lines.push(`YTD interest income,${kpis.ytdInterestIncome}`);
    lines.push(`Average loan size,${kpis.avgLoanSize}`);
    lines.push('');
    lines.push('Month,Disbursed,Disbursed count,Collected,Interest income,New borrowers');
    trends.forEach((t) => {
      lines.push(`${t.label},${t.disbursedAmount},${t.disbursedCount},${t.collectedAmount},${t.interestIncome},${t.newBorrowers}`);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `business_dashboard_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    if (!data) return;
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFontSize(16);
    doc.text('Business & Strategic Dashboard', 14, 16);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Generated: ${formatDateTimeDMY(data.generatedAt || Date.now())}  |  Window: last ${data.window?.months || months} months`, 14, 22);
    doc.setTextColor(0);

    autoTable(doc, {
      startY: 28,
      head: [['Key metric', 'Value']],
      body: [
        ['Active loans', toInt(kpis.activeLoans)],
        ['Active clients', toInt(kpis.activeClients)],
        ['Outstanding loan book', toCurrency(kpis.outstandingBook)],
        ['PAR 30', `${toCurrency(kpis.par30Amount)} (${toPct(kpis.par30Pct)})`],
        ['PAR 90', `${toCurrency(kpis.par90Amount)} (${toPct(kpis.par90Pct)})`],
        ['YTD disbursed', `${toCurrency(kpis.ytdDisbursedAmount)} (${toInt(kpis.ytdDisbursedCount)} loans)`],
        ['YTD collected', toCurrency(kpis.ytdCollectedAmount)],
        ['YTD interest income', toCurrency(kpis.ytdInterestIncome)],
        ['Average loan size', toCurrency(kpis.avgLoanSize)],
      ],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235] },
    });

    const charts = [
      { ref: disbCollRef, title: 'Disbursed vs Collected' },
      { ref: interestRef, title: 'Interest income' },
      { ref: agingRef, title: 'Loan book aging' },
      { ref: purposeRef, title: 'Disbursement by purpose' },
    ];
    let y = doc.lastAutoTable.finalY + 8;
    const imgW = (pageW - 14 * 2 - 8) / 2;
    const imgH = imgW * 0.6;
    let col = 0;
    charts.forEach((c) => {
      const inst = c.ref.current;
      if (!inst) return;
      let img;
      try { img = inst.toBase64Image(); } catch { img = null; }
      if (!img) return;
      if (y + imgH > doc.internal.pageSize.getHeight() - 14) { doc.addPage(); y = 16; col = 0; }
      const x = 14 + col * (imgW + 8);
      doc.setFontSize(9);
      doc.text(c.title, x, y);
      doc.addImage(img, 'PNG', x, y + 2, imgW, imgH);
      col += 1;
      if (col === 2) { col = 0; y += imgH + 12; }
    });

    if (col === 1) y += imgH + 12;
    if (y > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); y = 16; }
    autoTable(doc, {
      startY: y,
      head: [['Top borrowers', 'Loans', 'Outstanding']],
      body: (data.topBorrowers || []).map((b) => [b.name, toInt(b.loans), toCurrency(b.outstanding)]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save(`business_dashboard_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
        <div />
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value, 10))}
            
          >
            {WINDOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button onClick={() => load(months)} disabled={loading} className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-60">
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button onClick={exportPdf} disabled={!data} className="px-4 py-2 rounded-md bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm hover:bg-red-200 disabled:opacity-50">Export PDF</button>
          <button onClick={exportCsv} disabled={!data} className="px-4 py-2 rounded-md bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm hover:bg-green-200 disabled:opacity-50">Export CSV</button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{error}</div>}
      {loading && !data && <div className="text-sm text-gray-500 dark:text-slate-400">Loading dashboard…</div>}

      {data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-4">
            <KpiCard label="Active loans" value={toInt(kpis.activeLoans)} subtitle={`${toInt(kpis.activeClients)} active clients`} color="blue" onClick={loansWith('Active')} title="Show the active loans" />
            <KpiCard label="Outstanding book" value={toCurrency(kpis.outstandingBook)} color="blue" onClick={loansWith('Active')} title="Show the active loans" />
            <KpiCard label="PAR 30" value={toPct(kpis.par30Pct)} subtitle={toCurrency(kpis.par30Amount)} color={kpis.par30Pct > 10 ? 'red' : 'amber'} onClick={loansWith('Overdue')} title="Show the overdue loans" />
            <KpiCard label="PAR 90" value={toPct(kpis.par90Pct)} subtitle={toCurrency(kpis.par90Amount)} color={kpis.par90Pct > 5 ? 'red' : 'amber'} onClick={loansWith('Overdue')} title="Show the overdue loans" />
            <KpiCard label="Avg loan size" value={toCurrency(kpis.avgLoanSize)} color="violet" />
            <KpiCard label="YTD disbursed" value={toCurrency(kpis.ytdDisbursedAmount)} subtitle={`${toInt(kpis.ytdDisbursedCount)} loans`} color="green" />
            <KpiCard label="YTD collected" value={toCurrency(kpis.ytdCollectedAmount)} color="green" />
            <KpiCard label="YTD interest income" value={toCurrency(kpis.ytdInterestIncome)} color="violet" />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <ChartCard label="Disbursed vs Collected (N$)" color="blue" bodyClassName="h-64">
              <Line ref={disbCollRef} data={disbCollData} options={baseOptions} />
            </ChartCard>
            <ChartCard label="Interest income trend (N$)" color="violet" bodyClassName="h-64">
              <Line ref={interestRef} data={interestData} options={baseOptions} />
            </ChartCard>
            <ChartCard label="New borrowers per month" color="cyan" bodyClassName="h-64">
              <Bar ref={borrowersRef} data={borrowersData} options={baseOptions} />
            </ChartCard>
            <ChartCard label="Loan book aging (outstanding N$)" color="amber" bodyClassName="h-64">
              <Doughnut ref={agingRef} data={agingData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { color: '#94a3b8', boxWidth: 12 } } } }} />
            </ChartCard>
            <ChartCard label="Disbursement by purpose (N$)" color="amber" bodyClassName="h-64">
              <Bar ref={purposeRef} data={purposeData} options={{ ...baseOptions, indexAxis: 'y' }} />
            </ChartCard>
            <ChartCard label="Loans by collection method" color="teal" bodyClassName="h-64">
              <Doughnut
                data={{
                  labels: (breakdowns.byCollectionMethod || []).map((c) => c.method),
                  datasets: [{ data: (breakdowns.byCollectionMethod || []).map((c) => c.count), backgroundColor: PALETTE }],
                }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { color: '#94a3b8', boxWidth: 12 } } } }}
              />
            </ChartCard>
          </div>

          {/* Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard label="Portfolio by status" color="slate" flush>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className={TABLE_HEAD_ROW}>
                    {statusTh('Status', 'status', (r) => r.status, RH)}
                    {statusTh('Loans', 'count', (r) => Number(r.count), RH_RIGHT)}
                    {statusTh('Outstanding', 'outstanding', (r) => Number(r.outstanding), RH_RIGHT)}
                  </tr>
                </thead>
                <tbody>
                  {sortRows(breakdowns.byStatus || [], statusSort).map((r) => (
                    <tr key={r.status} className="border-b border-gray-200 dark:border-slate-700">
                      <td className="px-4 py-1.5">{canViewLoans
                        ? <button type="button" onClick={loansWith(r.status)} className={LINK_TEXT} title={`Show the ${r.status} loans`}>{r.status}</button>
                        : r.status}</td>
                      <td className="px-4 py-1.5 text-right">{toInt(r.count)}</td>
                      <td className="px-4 py-1.5 text-right">{toCurrency(r.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ChartCard>

            <ChartCard label="Top borrowers by outstanding" color="slate" flush>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className={TABLE_HEAD_ROW}>
                    {borrowerTh('Client', 'client', (b) => b.name, RH)}
                    {borrowerTh('Loans', 'loans', (b) => Number(b.loans), RH_RIGHT)}
                    {borrowerTh('Outstanding', 'outstanding', (b) => Number(b.outstanding), RH_RIGHT)}
                  </tr>
                </thead>
                <tbody>
                  {sortRows(data.topBorrowers || [], borrowerSort).map((b) => (
                    <tr key={b.clientId} className="border-b border-gray-200 dark:border-slate-700">
                      <td className="px-4 py-1.5"><ClientLink clientId={b.clientId} name={b.name} /></td>
                      <td className="px-4 py-1.5 text-right">{toInt(b.loans)}</td>
                      <td className="px-4 py-1.5 text-right">{toCurrency(b.outstanding)}</td>
                    </tr>
                  ))}
                  {(!data.topBorrowers || data.topBorrowers.length === 0) && (
                    <tr><td colSpan={3} className="px-4 py-3 text-center text-gray-400 italic">No outstanding loans.</td></tr>
                  )}
                </tbody>
              </table>
            </ChartCard>

            <ChartCard label="Monthly performance" color="slate" flush className="lg:col-span-2">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className={TABLE_HEAD_ROW}>
                      {monthTh('Month', 'month', (t) => t.ym, RH)}
                      {monthTh('Disbursed', 'disbursed', (t) => Number(t.disbursedAmount), RH_RIGHT)}
                      {monthTh('Loans', 'loans', (t) => Number(t.disbursedCount), RH_RIGHT)}
                      {monthTh('Collected', 'collected', (t) => Number(t.collectedAmount), RH_RIGHT)}
                      {monthTh('Interest', 'interest', (t) => Number(t.interestIncome), RH_RIGHT)}
                      {monthTh('New borrowers', 'borrowers', (t) => Number(t.newBorrowers), RH_RIGHT)}
                    </tr>
                  </thead>
                  <tbody>
                    {sortRows(trends, monthSort).map((t) => (
                      <tr key={t.ym} className="border-b border-gray-200 dark:border-slate-700">
                        <td className="px-4 py-1.5">{t.label}</td>
                        <td className="px-4 py-1.5 text-right">{toCurrency(t.disbursedAmount)}</td>
                        <td className="px-4 py-1.5 text-right">{toInt(t.disbursedCount)}</td>
                        <td className="px-4 py-1.5 text-right">{toCurrency(t.collectedAmount)}</td>
                        <td className="px-4 py-1.5 text-right">{toCurrency(t.interestIncome)}</td>
                        <td className="px-4 py-1.5 text-right">{toInt(t.newBorrowers)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
